import { useState, useEffect, useMemo } from 'react';
import apiServiceDashboard from '../../services/apiService.dashboards';
import RestoreIcon from '@mui/icons-material/Restore';
import Typography from '@mui/material/Typography';
import { MaterialReactTable } from 'material-react-table';
import { useCompanyId } from '../../services/CompanyidProvider';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import styles from './dashboard.module.css';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';

function Dashboard(props) {
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [permissions, setPermssions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedId(null);
    fetchData(companyId);
  };
  const { companyId } = useCompanyId();
  const handleConfirmRestore = () => {
    if (selectedId !== null) {
      setIsLoading(true);
      // Call the API service to restore the item
      apiServiceDashboard.restorecustombyid(selectedId)
        .then(() => {
          // Update the dashboard data after successful restore
          setDashboardData(dashboardData ? 
            { ...dashboardData, data: dashboardData.data.filter(item => item.id !== selectedId) } : 
            null
          );
          handleDialogClose();
        })
        .catch(error => {
          console.error('Error restoring item:', error);
          handleDialogClose();
        });
    }
  };
  const fetchData = (companyId) => {
    setIsLoading(true);
    apiServiceDashboard.getDeletedCustomDashboards(companyId)
      .then(response => {
        setDashboardData(response);
      })
      .catch(error => {
        console.error('Error fetching dashboard data:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  useEffect(() => {
    // Make a GET request to fetch dashboard data using apiServiceDashboard
    fetchData(companyId)
  }, [companyId]);
  
  const columns = useMemo(
    () => [
      {
        header: 'Restore',
        size: 80,
        Cell: ({ row }) => (
          <Tooltip arrow placement="right" title="Restore">
            <IconButton 
              className={styles.restoreButton}
              onClick={() => handleDeleteClick(row.original.id)}
              size="small"
            >
              <RestoreIcon /> 
            </IconButton>
          </Tooltip>
        ),
      },
      {
        header: 'id',
        accessorKey: 'id', //simple accessorKey pointing to flat data
      },
      {
        header: 'Title',
        accessorKey: 'name', //simple accessorKey pointing to flat data
      },
      {
        header: 'UpdatedAt',
        accessorKey: 'updatedAt', //simple accessorKey pointing to flat data
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return 'N/A';
          try {
            const date = new Date(value);
            // Use local date and time format based on user's browser locale
            return date.toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'medium'
            });
          } catch (e) {
            return value || 'N/A';
          }
        }
      },
      
    ],
    [],
  );

  useEffect(() => {
    const jwtToken = getJwtToken();


    if (jwtToken) {
      const { permissions } = jwt_decode(jwtToken);
      setPermssions(permissions);
    }
  }, []);
  if (!permissions || !permissions.includes('read')) {
    // If user does not have permission, you can render a message, redirect, or display a different component
    return <div>You do not have permission to access this page.</div>;
  }
  return (<>
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.headerRow}>
          <Button 
            className={styles.backButton}
            onClick={() => window.history.back()}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Typography variant="h6" color="black" className={styles.tableHeader}>
            Restore Dashboards
          </Typography>
        </div>
      </div>
      
      {/* Render the fetched dashboard data */}
      <Paper className={styles.tableContainer}>
        <div className={styles.tableWrapper}>
          <MaterialReactTable
            columns={columns}
            data={dashboardData?.data || []}
            //enable some features
            enableColumnOrdering
            enableColumnResizing
            enableGlobalFilter={true}
            enableColumnFilters={false}
            enableStickyHeader
            enableRowActions={false}
            muiTableContainerProps={{ 
              className: styles.expandableTable
            }}
            muiTablePaperProps={{ elevation: 0 }}
            muiSearchTextFieldProps={{
              variant: 'outlined',
              size: 'small',
              placeholder: 'Search dashboards...',
              className: styles.searchField
            }}
            initialState={{ 
              density: 'compact',
              columnVisibility: { 
                id: true
              },
              pagination:{pageSize:15}
            }}
            state={{
              isLoading: isLoading,
            }}
            renderEmptyRowsFallback={() => (
              <Typography 
                sx={{ py: 6 }} 
                align="center" 
                color="text.secondary"
              >
                {isLoading ? "Loading..." : "No dashboards found"}
              </Typography>
            )}
            muiLinearProgressProps={{
              sx: {
                display: 'none', // Hide the linear progress bar completely
              },
            }}
          />
        </div>
      </Paper>
    </div>
    {/* Confirmation Dialog */}
    <Dialog
      open={openDialog}
      onClose={handleDialogClose}
      PaperProps={{
        style: { borderRadius: '8px' }
      }}
    >
      <DialogTitle className={styles.dialogTitle}>Confirm Restore</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <DialogContentText>
          Are you sure you want to RESTORE this item?
        </DialogContentText>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button 
          onClick={handleDialogClose} 
          className={styles.dialogCancelButton}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirmRestore} 
          className={styles.dialogSubmitButton}
        >
          Restore
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
}


Dashboard.propTypes = {};

Dashboard.defaultProps = {};

export default Dashboard;
